import { BlockLoading } from '@/client/components/fetch-status/block-loading';
import { api } from '@/client/utils/api';
import { useEventDetail } from '@/libs/react-sdk/api/use-event-detail';
import { ClaimStatus } from '@/libs/sdk/api';
import { EventCard } from '../event-card';
import { MarketCard } from '../market-card';
import { ResolvedMarketChartCard } from './market-chart-card';
import type { Market, EventDetailMarket, MarketDetail, MarketDetailOption, MarketOption, FollowStatus, MarketStatus } from '@/libs/sdk/api';
import { getEventSectionCardVariant } from './event-section-card-variant';

type Props = {
  eventId: string;
  className?: string;
  isHero?: boolean;
};

export function adaptEventDetailMarket(m: EventDetailMarket, eventId: string, baseTokenAddress: string): Market {
  const options: MarketOption[] = (m.options || []).map(opt => ({
    decimal: opt.decimal,
    tokenAddress: opt.address,
    tokenDescription: opt.description,
    tokenName: opt.name,
    tokenPicUrl: opt.picUrl,
    tokenPrice: opt.price,
  }));

  return {
    marketAddress: m.address,
    marketName: m.name,
    nameI18n: m.nameI18n,
    marketPicUrl: m.picUrl,
    marketDescription: m.description,
    marketStatus: m.status as MarketStatus,
    marketVolume: m.volume,
    marketDecimal: String(m.decimal),
    participantsCount: m.participantsCount,
    options,
    isFollowed: 0 as FollowStatus,
    createdAt: m.createdAt,
    deadline: m.deadline,
    eventId,
    result: '',
    baseTokenAddress,
  };
}

function adaptEventDetailMarketToDetail(m: EventDetailMarket, baseTokenAddress: string): MarketDetail {
  const optionList: MarketDetailOption[] = (m.options || []).map(opt => ({
    optionAddress: opt.address,
    optionName: opt.name,
    optionSymbol: opt.symbol,
    optionPicUrl: opt.picUrl,
    description: opt.description,
    price: opt.price,
    decimal: opt.decimal,
  }));

  return {
    marketAddress: m.address,
    marketName: m.name,
    nameI18n: m.nameI18n,
    marketPicUrl: m.picUrl,
    marketDescription: m.description,
    deadline: String(m.deadline),
    marketStatus: m.status as MarketStatus,
    volume: m.volume,
    decimal: m.decimal,
    participantsCount: m.participantsCount,
    resultOptionAddress: m.result,
    rule: '',
    ruleFileKey: '',
    isFollowed: 0 as FollowStatus,
    optionList,
    isClaim: ClaimStatus.no,
    baseTokenAddress,
  };
}

export const EventSectionCard = ({ eventId, className, isHero = false }: Props) => {
  const { data: eventDetail, isLoading } = useEventDetail(api, { eventId });

  if (isLoading) return <BlockLoading />;
  if (!eventDetail?.markets?.length) return null;

  const markets = eventDetail.markets.map(m => adaptEventDetailMarket(m, eventId, eventDetail.collateral));
  const variant = getEventSectionCardVariant({
    markets: eventDetail.markets,
    isHero,
  });

  if (variant === 'heroChart') {
    return <ResolvedMarketChartCard className={className} market={adaptEventDetailMarketToDetail(eventDetail.markets[0], eventDetail.collateral)} />;
  }

  if (variant === 'compactMarket') {
    return <MarketCard market={markets[0]} className={className} />;
  }

  return <EventCard eventId={eventId} markets={markets} className={className} />;
};
